import { useState, useEffect } from "react";
import "@/App.css";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Smartphone, 
  Globe, 
  ShieldCheck, 
  ArrowRight, 
  Menu, 
  X, 
  Mail,
  ChevronRight
} from "lucide-react";
import { Button } from "./components/ui/button";

// Constants
const LOGO_URL = "https://customer-assets.emergentagent.com/job_global-services-3/artifacts/pk62ddid_SimpleWorks-Logo%20%284%29.png";
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1521202850558-0110494d0457?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  app: "https://images.unsplash.com/photo-1628251766155-6292b0b5f2d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  trading: "https://images.unsplash.com/photo-1751970046974-6d5a27f23ce1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  fire: "https://images.unsplash.com/photo-1512302064969-4e1927b463fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", href: "#services" },
    { name: "About", href: "#about" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 navbar-blur border-b border-slate-100 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" data-testid="logo-link" className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="The Simple Works" 
              className="h-10 w-auto"
              data-testid="logo-image"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className="text-slate-600 hover:text-slate-900 text-sm font-medium link-underline transition-colors"
              >
                {link.name}
              </a>
            ))}
            <a href="https://thesimpleapps.dev" target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="nav-cta-button"
                className="bg-slate-900 text-white hover:bg-teal-600 transition-colors duration-300 rounded-md px-6 py-2 text-sm"
              >
                View Our Apps
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-slate-900"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white border-b border-slate-100"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="block text-slate-600 hover:text-slate-900 text-base font-medium"
              >
                {link.name}
              </a>
            ))}
            <a 
              href="https://thesimpleapps.dev" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-slate-900 text-white hover:bg-teal-600 rounded-md py-3">
                View Our Apps
              </Button>
            </a>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="min-h-screen flex items-center pt-20 bg-white relative overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute right-0 top-0 w-1/2 h-full hidden lg:block">
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-white z-10" />
        <img 
          src={IMAGES.hero}
          alt="Abstract architecture"
          className="w-full h-full object-cover opacity-30"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <AnimatedSection>
              <motion.span 
                variants={fadeInUp}
                className="caption text-teal-600 mb-4 block"
              >
                US-Registered Company
              </motion.span>
              
              <motion.h1 
                variants={fadeInUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] text-slate-900 mb-6"
                data-testid="hero-headline"
              >
                Simple Digital Solutions for a Smarter World
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-lg md:text-xl leading-relaxed font-light text-slate-600 mb-10 max-w-2xl"
                data-testid="hero-subheadline"
              >
                We build practical mobile apps, deliver global trade solutions, and provide professional fire safety engineering services.
              </motion.p>
              
              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4"
              >
                <a href="https://thesimpleapps.dev" target="_blank" rel="noopener noreferrer">
                  <Button 
                    data-testid="hero-cta-primary"
                    className="bg-slate-900 text-white hover:bg-teal-600 transition-colors duration-300 rounded-md px-8 py-4 text-sm font-medium tracking-wide btn-primary relative z-10"
                  >
                    View Our Apps
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href="#contact">
                  <Button 
                    data-testid="hero-cta-secondary"
                    variant="outline"
                    className="border-slate-200 text-slate-900 hover:border-slate-900 transition-colors duration-300 rounded-md px-8 py-4 text-sm font-medium tracking-wide"
                  >
                    Contact Us
                  </Button>
                </a>
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
};

// Service Card Component
const ServiceCard = ({ icon: Icon, title, description, features, link, linkText, isLarge = false, image }) => {
  return (
    <motion.div
      variants={fadeInUp}
      className={`service-card bg-white border border-slate-200 p-8 hover:border-teal-600/50 group relative overflow-hidden ${
        isLarge ? "bento-large" : ""
      }`}
      data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Background Image Overlay */}
      {image && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500">
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-6">
          <div className="w-12 h-12 rounded-lg bg-slate-50 flex items-center justify-center service-icon transition-transform duration-300">
            <Icon className="h-6 w-6 text-teal-600" />
          </div>
          <span className="caption text-slate-400">0{isLarge ? "1" : "2"}</span>
        </div>
        
        <h3 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mb-4">
          {title}
        </h3>
        
        <p className="text-base leading-relaxed text-slate-600 mb-6">
          {description}
        </p>
        
        <ul className="space-y-3 mb-8">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-3 text-sm text-slate-600">
              <ChevronRight className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        
        {link && (
          <a 
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium text-sm group/link"
            data-testid={`service-link-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {linkText}
            <ArrowRight className="ml-2 h-4 w-4 service-arrow transition-transform duration-300" />
          </a>
        )}
      </div>
    </motion.div>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Smartphone,
      title: "Mobile App Development & Publishing",
      description: "We design, develop, and publish mobile applications that solve real-world problems and enhance daily productivity.",
      features: [
        "Design, development, and publishing of mobile applications",
        "Focus on productivity, finance tracking, utilities management, and everyday digital tools",
        "Apps distributed globally via Google Play and other platforms",
        "Ongoing maintenance, updates, and customer support"
      ],
      link: "https://thesimpleapps.dev",
      linkText: "Explore Our Apps",
      isLarge: true,
      image: IMAGES.app
    },
    {
      icon: Globe,
      title: "International Trading & Sourcing",
      description: "Global trading expertise connecting suppliers with clients across continents.",
      features: [
        "Global trading of industrial, safety, and general products",
        "Supplier sourcing, export coordination, and compliance support",
        "Serving clients across Asia, Middle East, Europe, and Africa"
      ],
      isLarge: false,
      image: IMAGES.trading
    },
    {
      icon: ShieldCheck,
      title: "Fire Safety Engineering & Protection Systems",
      description: "Professional fire safety consultancy and system solutions for industrial and commercial projects.",
      features: [
        "Fire safety engineering consultancy and system solutions",
        "Design and supply of fire protection systems and equipment",
        "Experience in industrial, warehouse, and commercial projects"
      ],
      link: "https://defirebd.com",
      linkText: "Visit Our Fire Safety Division",
      isLarge: false,
      image: IMAGES.fire
    }
  ];

  return (
    <section 
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="mb-16">
            <span className="caption text-teal-600 mb-4 block">What We Do</span>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900">
              Our Services
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, idx) => (
              <ServiceCard key={idx} {...service} />
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section 
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.span variants={fadeInUp} className="caption text-teal-600 mb-4 block">
                About Us
              </motion.span>
              <motion.h2 
                variants={fadeInUp}
                className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6"
                data-testid="about-title"
              >
                About The Simple Works
              </motion.h2>
              <motion.div variants={fadeInUp} className="space-y-6">
                <p className="text-lg leading-relaxed text-slate-600">
                  The Simple Works is a diversified business unit of Simple Work LLC, a company registered in the United States. We operate remotely, serving clients across the globe with a commitment to practical solutions and long-term partnerships.
                </p>
                <p className="text-base leading-relaxed text-slate-600">
                  Our focus spans mobile application development, international trade facilitation, and fire safety engineering. We believe in delivering value through simplicity, compliance, and reliability.
                </p>
              </motion.div>
            </div>
            
            <motion.div 
              variants={fadeInUp}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-slate-50 p-8 border border-slate-200">
                <div className="text-4xl font-semibold text-teal-600 mb-2">US</div>
                <div className="text-sm text-slate-600">Registered Company</div>
              </div>
              <div className="bg-slate-50 p-8 border border-slate-200">
                <div className="text-4xl font-semibold text-teal-600 mb-2">3+</div>
                <div className="text-sm text-slate-600">Business Divisions</div>
              </div>
              <div className="bg-slate-50 p-8 border border-slate-200">
                <div className="text-4xl font-semibold text-teal-600 mb-2">4+</div>
                <div className="text-sm text-slate-600">Continents Served</div>
              </div>
              <div className="bg-slate-50 p-8 border border-slate-200">
                <div className="text-4xl font-semibold text-teal-600 mb-2">100%</div>
                <div className="text-sm text-slate-600">Remote Operations</div>
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section 
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-slate-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection>
          <div className="max-w-3xl mx-auto text-center">
            <motion.span variants={fadeInUp} className="caption text-teal-400 mb-4 block">
              Get In Touch
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-medium tracking-tight text-white mb-6"
              data-testid="contact-title"
            >
              Contact Us
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg leading-relaxed text-slate-400 mb-10">
              We operate digitally and serve clients worldwide. Reach out to discuss how we can help with your next project.
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <a 
                href="mailto:support@thesimpleworks.com"
                data-testid="contact-email-link"
                className="inline-flex items-center gap-3 bg-white text-slate-900 hover:bg-teal-600 hover:text-white transition-colors duration-300 rounded-md px-8 py-4 font-medium text-base"
              >
                <Mail className="h-5 w-5" />
                support@thesimpleworks.com
              </a>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer 
      data-testid="footer"
      className="py-12 bg-slate-950 border-t border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="The Simple Works" 
              className="h-8 w-auto brightness-0 invert"
              data-testid="footer-logo"
            />
          </div>
          
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <span className="text-slate-700">|</span>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
          </div>
          
          <div className="text-sm text-slate-500">
            © {new Date().getFullYear()} Simple Work LLC. All services provided remotely.
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App" data-testid="app-container">
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
